import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/homeData";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-lg border-b border-background-200/60"
          : "bg-background-50/85 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-[72px]">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-1.5 cursor-pointer"
        >
          <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            hoobuy<span className="text-primary-500">.</span>christmas
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6">
          {/* Home */}
          <Link
            to="/"
            className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.home")}
          </Link>

          {/* Categories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>{t("common.categories")}</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[560px] bg-background-50 border border-background-200/70 rounded-2xl py-3 px-3 grid grid-cols-2 gap-1 z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg overflow-hidden shrink-0 bg-background-100">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </span>
                    <span className="font-medium whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Shopping Guide */}
          <button
            onClick={() => navigate("/artigo/guia-completo-hoobuyspreadsheet")}
            className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.shoppingGuide")}
          </button>

          {/* Articles */}
          <button
            onClick={() => navigate("/artigo/guia-completo-hoobuyspreadsheet")}
            className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.readGuide")}
          </button>

          {/* CTA Button */}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.buyItNow")}
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu - Fullscreen overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-6 flex flex-col gap-1">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-xl text-foreground-500"></i>
              <span>{t("common.home")}</span>
            </Link>

            {/* Shopping Guide */}
            <button
              onClick={() => {
                navigate("/artigo/guia-completo-hoobuyspreadsheet");
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer text-left"
            >
              <i className="ri-book-open-line text-xl text-foreground-500"></i>
              <span>{t("common.shoppingGuide")}</span>
            </button>

            {/* Categories toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-base font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer mt-1"
            >
              <div className="flex items-center gap-3">
                <i className="ri-grid-line text-xl text-foreground-500"></i>
                <span>{t("common.categories")}</span>
              </div>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {/* Mobile Category Grid */}
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1.5 px-2 mt-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-lg overflow-hidden shrink-0 bg-background-100">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </span>
                    <span className="font-medium text-xs leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-background-200/60 my-3"></div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mx-4 mt-2 px-6 py-4 bg-foreground-950 text-background-50 text-base font-semibold rounded-full hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              <span>{t("common.buyItNow")}</span>
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}